import React, { useRef, useState } from 'react';
import { Stack } from '@mui/material';
import { Column as TColumn, IBoard, Item as IItem } from './interfaces';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import { Column } from '../Column';
import { Item } from '../Item';

export const Board: React.FC<IBoard> = ({
  columns,
  setColumns,
  handleItemColumnChange,
  handleItemDeletion,
  handleItemEdit,
  handleItemClose,
}): JSX.Element => {
  const previousColumnsRef = useRef<TColumn[] | null>(null);
  const [activeItem, setActiveItem] = useState<IItem | null | undefined>(null);
  const findColumn = (itemId: string | number) =>
    columns.find(
      (col) => col.items && col.items.some((item) => item.id === itemId)
    );

  const getColumn = (columnId: string | number) =>
    columns.find((col) => col.id === columnId);

  const getItemWithId = (itemId: string | number): IItem | undefined => {
    for (const col of columns) {
      if (!col.items) continue;

      const found = col.items.find((item) => item.id === itemId);

      if (found) {
        return found;
      }
    }

    return undefined;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveItem(getItemWithId(event.active.id));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveItem(null);

    if (!over) {
      return;
    }

    const fromColumn = findColumn(active.id);
    const toColumn = getColumn(over.id);

    if (!fromColumn || !toColumn) return;

    previousColumnsRef.current = columns;

    if (fromColumn === toColumn) {
      console.log('SAME COLUMN');
    } else {
      console.log(
        `OTHER COLUMN: From ${fromColumn.title} to ${toColumn.title}`
      );

      setColumns((prev) => {
        const fromColumnIndex = prev.findIndex(
          (col) => col.id === fromColumn.id
        );
        const toColumnIndex = prev.findIndex((col) => col.id === toColumn.id);

        if (fromColumnIndex === -1 || toColumnIndex === -1) {
          return prev;
        }

        const fromColumnObj = prev[fromColumnIndex];
        const toColumnObj = prev[toColumnIndex];

        const fromItems = fromColumnObj.items ?? [];
        const toItems = toColumnObj.items ?? [];

        const movingItem = fromItems.find((item) => item.id === active.id);

        if (!movingItem) {
          return prev;
        }

        const newColumns = [...prev];

        newColumns[fromColumnIndex] = {
          ...fromColumnObj,
          items: fromItems.filter((item) => item.id !== active.id),
        };

        newColumns[toColumnIndex] = {
          ...toColumnObj,
          items: [...toItems, movingItem],
        };

        return newColumns;
      });

      const success = await handleItemColumnChange(active.id, over.id);

      if (!success && previousColumnsRef.current) {
        setColumns(previousColumnsRef.current);
      }
    }
  };

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <Stack
        direction="row"
        columnGap="5px"
        sx={{
          flex: 1,
          overflowX: 'auto',
          flexWrap: 'nowrap',
        }}
      >
        {columns.map((columnData) => {
          return (
            <Column
              key={columnData.id}
              id={columnData.id}
              title={columnData.title}
              color={columnData.color}
            >
              {columnData.items &&
                columnData.items.map((item) => {
                  return (
                    <Item
                      {...item}
                      onDelete={() => handleItemDeletion(item)}
                      onEdit={() => handleItemEdit(item.id)}
                      onCloseOpportunity={() => handleItemClose(item)}
                    />
                  );
                })}
            </Column>
          );
        })}
      </Stack>
      <DragOverlay>{activeItem && <Item {...activeItem} />}</DragOverlay>
    </DndContext>
  );
};
